/**
 * Elation's note-section vocabulary — the HOST's half of the layering rule.
 *
 * `@astrana/ui` models a clinical note. This file maps that model onto ONE EHR's
 * section keys. The table used to live inside the library, where every
 * Elation-specific discovery forced a library release and a second EHR would have
 * meant a second vendor table inside a package several frontends consume.
 *
 * A second EHR now costs a second table next to this one — no library change.
 *
 * ── PROVENANCE ──────────────────────────────────────────────────────────────
 * Keys below come from `window.el8.appchart.visitNote.main._inputSections` read
 * live against a real Elation sandbox chart on 2026-08-04/05. Two templates were
 * observed:
 *
 *   Legacy SOAP:  reason, problem, objective, assessment, plan, followup
 *                 (textarea); pe, ros, procedure (listeditor); instr
 *                 (multilist); referenced, orders (textlist)
 *   Simple Note:  reason, narrative, followup (textarea); procedure
 *                 (listeditor); instr (multilist); referenced, orders (textlist)
 *
 * Neither template has `hpi`, `subjective` or `narrative` on the SOAP side.
 * `hpi`/`subjective` are kept as candidates anyway because a practice-authored
 * template may define them and they are the correct first choice where they
 * exist — but they were NOT observed live, and only the keys named in the two
 * lists above are verified.
 *
 * Anything not listed here is never proposed. Structured sections are excluded
 * on two independent grounds: the host's read marks them non-writable, and the
 * library only proposes sections whose `writable` is explicitly `true`.
 */

/**
 * The canonical note fields the library can propose. Structurally identical to
 * `VisitNoteField` in `@astrana/ui/scribe`; declared here so this table (and its
 * tests) do not depend on a library version that has shipped the
 * `SectionCandidateMap` export yet. `elationSectionCandidates()` is typed
 * against the library's own type at the call site once the pin is bumped.
 */
export type CanonicalNoteField =
  | "chiefComplaint"
  | "historyOfPresentIllness"
  | "reviewOfSystems"
  | "physicalExam"
  | "results"
  | "assessment"
  | "plan"
  | "patientInstructions"
  | "followUp"
  | "visitNote";

/**
 * Candidates per field, MOST SPECIFIC FIRST. The order is load-bearing twice
 * over: it is the fallback chain for one field, and the tiebreak when two fields
 * could take the same section (the library keeps only the most specific
 * claimant, because the write seam APPENDS).
 */
export const ELATION_SECTION_CANDIDATES: Record<
  CanonicalNoteField,
  readonly string[]
> = {
  chiefComplaint: ["reason", "cc", "chiefcomplaint", "subjective", "narrative"],

  /**
   * `problem` is LAST on purpose, and it is the reason this field maps at all.
   *
   * On the legacy SOAP template none of the first three exist — no `hpi`, no
   * `subjective`, no `narrative` — so the history of present illness had nowhere
   * to go and silently fell through to "no target" on the one template we
   * actually ship against. There, singular `problem` IS the subjective slot:
   * live-verified `getType() === "textarea"`, rendered under the label "Prob:",
   * and the shipped extension scraper already reads it as the note's subjective.
   *
   * Ranked below the specific keys so a template that HAS a real `hpi` or
   * `subjective` still wins. PLURAL `problems`/`problemlist` are the structured
   * problem list and are deliberately absent: clinical prose appended into a
   * structured problem list is a mis-filed clinical record. The write-side
   * blocklist in `visitNoteWriteSection` is the independent guard on that.
   */
  historyOfPresentIllness: ["hpi", "subjective", "narrative", "problem"],

  /** Same shape as `physicalExam` below — `ros` is a checklist listeditor too. */
  reviewOfSystems: ["ros", "objective", "narrative"],

  /**
   * `pe` exists on both the SOAP and H&P templates and is a `listeditor` on BOTH
   * (live-verified 2026-08-06). It is FIRST because it is the correct destination,
   * and the write to it works: the production path (`_model.addBullet("Pe", …)`
   * then `pe.addEntry(…)`) lands a bullet that renders and reads back —
   * `numEntries: 1`, `getJsonData()` returning it. `Pe` is a valid category per
   * `_model.getBulletSets()`.
   *
   * Two claims about `pe` were made here and are both retracted, because someone
   * will otherwise re-derive them:
   *
   *  - "the host marks it non-writable and it is never proposed" — it did not.
   *    The type gate refuses `textlist` and non-`instr` `multilist`; a `listeditor`
   *    passes. `pe` was writable, and being first here it was the primary exam
   *    destination, so the `objective`/`narrative` fall-through never happened.
   *  - "a bullet cannot reach the exam at all" — it does reach the section. The
   *    measurement behind that was taken synchronously, and the listeditor renders
   *    on save/refresh, so a zero-entry read straight after the write meant "not
   *    yet", not "never".
   *
   * What IS true: the exam's own content lives in 16
   * `textarea[name="pe-<System>"]` checklist fields that a bullet does not merge
   * into and `getJsonData()` never sees. A bullet therefore sat ALONGSIDE the
   * template's per-system normals as one blob — the reported defect. `stageInPage`
   * now writes each system into its own field for `pe` and `ros` (see
   * `planChecklistWrite`), which makes `pe` not just the section a clinician looks
   * in for the exam but the one that renders it correctly.
   * `objective`/`narrative` remain the fall-through for templates without it.
   */
  physicalExam: ["pe", "objective", "narrative"],

  /**
   * `objective` (SOAP, textarea) and `data` (H&P, listeditor) are the real
   * results sections — both live-verified 2026-08-06. `data` is structured, so
   * the host refuses it; `objective` takes the write on SOAP.
   */
  results: ["objective", "data", "narrative"],

  assessment: ["assessment", "assessplan", "narrative"],
  plan: ["plan", "assessplan", "narrative"],
  patientInstructions: [
    "instr",
    "instructions",
    "patientinstructions",
    "narrative",
  ],
  followUp: ["followup", "narrative"],
  /** The whole note only ever belongs in a single-narrative section. */
  visitNote: ["narrative", "assessplan"],
};

/**
 * The mapping in the shape the library takes. Returns a fresh mutable copy so a
 * caller cannot mutate the table, and so the readonly arrays above do not force
 * `readonly` into the library's prop type.
 */
export function elationSectionCandidates(): Record<
  CanonicalNoteField,
  string[]
> {
  return Object.fromEntries(
    Object.entries(ELATION_SECTION_CANDIDATES).map(([field, keys]) => [
      field,
      [...keys],
    ]),
  ) as Record<CanonicalNoteField, string[]>;
}
